/**
 * AI automation ROI model aligned with The Automators ROI calculator
 * (manual hours reclaimed + error-reduction savings, illustrative implementation budget).
 */

import {
  parsePercent,
  parseWholeNumber,
  type TrafficCalculatorSliderRange,
} from "@/components/services/seo/seo-revenue-projection";

export type HubSpotRoiField = "employees" | "hoursPerWeek" | "hourlyCost" | "errorRate";

export type HubSpotRoiIndustryId =
  | "custom"
  | "oil-gas"
  | "healthcare"
  | "professional-services"
  | "ecommerce-retail"
  | "manufacturing"
  | "technology-saas"
  | "finance-insurance"
  | "real-estate";

export type HubSpotRoiPresetDefaults = {
  employees: number;
  hoursPerWeek: number;
  hourlyCost: number;
  errorRate: number;
  automationEfficiency: number;
};

export type HubSpotRoiIndustryPreset = {
  value: HubSpotRoiIndustryId;
  defaults: HubSpotRoiPresetDefaults;
};

/** Same presets and defaults as theautomators.ai/tools/roi-calculator */
export const HUBSPOT_ROI_INDUSTRY_PRESETS: readonly HubSpotRoiIndustryPreset[] = [
  {
    value: "custom",
    defaults: {
      employees: 10,
      hoursPerWeek: 15,
      hourlyCost: 35,
      errorRate: 5,
      automationEfficiency: 0.7,
    },
  },
  {
    value: "oil-gas",
    defaults: {
      employees: 25,
      hoursPerWeek: 20,
      hourlyCost: 55,
      errorRate: 8,
      automationEfficiency: 0.75,
    },
  },
  {
    value: "healthcare",
    defaults: {
      employees: 15,
      hoursPerWeek: 18,
      hourlyCost: 40,
      errorRate: 10,
      automationEfficiency: 0.6,
    },
  },
  {
    value: "professional-services",
    defaults: {
      employees: 12,
      hoursPerWeek: 15,
      hourlyCost: 50,
      errorRate: 5,
      automationEfficiency: 0.65,
    },
  },
  {
    value: "ecommerce-retail",
    defaults: {
      employees: 10,
      hoursPerWeek: 20,
      hourlyCost: 30,
      errorRate: 7,
      automationEfficiency: 0.75,
    },
  },
  {
    value: "manufacturing",
    defaults: {
      employees: 20,
      hoursPerWeek: 25,
      hourlyCost: 35,
      errorRate: 12,
      automationEfficiency: 0.75,
    },
  },
  {
    value: "technology-saas",
    defaults: {
      employees: 8,
      hoursPerWeek: 12,
      hourlyCost: 60,
      errorRate: 4,
      automationEfficiency: 0.7,
    },
  },
  {
    value: "finance-insurance",
    defaults: {
      employees: 15,
      hoursPerWeek: 18,
      hourlyCost: 50,
      errorRate: 6,
      automationEfficiency: 0.65,
    },
  },
  {
    value: "real-estate",
    defaults: {
      employees: 8,
      hoursPerWeek: 15,
      hourlyCost: 35,
      errorRate: 5,
      automationEfficiency: 0.7,
    },
  },
] as const;

export const HUBSPOT_ROI_WORKING_WEEKS_PER_YEAR = 50;
export const HUBSPOT_ROI_ERROR_COST_MULTIPLIER = 1.5;
export const HUBSPOT_ROI_IMPL_BUDGET_MIN = 15_000;
export const HUBSPOT_ROI_IMPL_BUDGET_MAX = 50_000;

export type HubSpotRoiInputs = {
  employeesRaw: string;
  hoursPerWeekRaw: string;
  hourlyCostRaw: string;
  errorRateRaw: string;
  automationEfficiency: number;
};

export type HubSpotRoiKpis = {
  totalManualHoursAnnual: number;
  annualHoursSaved: number;
  labourSavings: number;
  errorReductionSavings: number;
  annualCostSavings: number;
  estimatedImplementationCost: number;
  roiPercentage: number;
  paybackMonths: number;
};

export type HubSpotRoiProjection =
  | { kind: "incomplete"; empty: HubSpotRoiField[]; invalid: HubSpotRoiField[] }
  | {
      kind: "value";
      kpis: HubSpotRoiKpis;
    };

/** Slider bounds aligned with The Automators ROI calculator (query params e, h, c, r). */
export const HUBSPOT_ROI_SLIDER_RANGES = {
  employees: { min: 1, max: 100, step: 1, dragStep: 1, fallback: 10 },
  hoursPerWeek: { min: 1, max: 40, step: 1, dragStep: 1, fallback: 15 },
  hourlyCost: { min: 15, max: 150, step: 5, fallback: 35 },
  errorRate: { min: 0, max: 25, step: 1, dragStep: 1, fallback: 5 },
} as const satisfies Record<HubSpotRoiField, TrafficCalculatorSliderRange>;

export function getHubSpotRoiIndustryPreset(
  id: HubSpotRoiIndustryId,
): HubSpotRoiIndustryPreset {
  return (
    HUBSPOT_ROI_INDUSTRY_PRESETS.find((preset) => preset.value === id) ??
    HUBSPOT_ROI_INDUSTRY_PRESETS[0]
  );
}

export function projectHubSpotRoi(inputs: HubSpotRoiInputs): HubSpotRoiProjection {
  const parsed: Record<HubSpotRoiField, number | null> = {
    employees: parseWholeNumber(inputs.employeesRaw),
    hoursPerWeek: parseWholeNumber(inputs.hoursPerWeekRaw),
    hourlyCost: parseWholeNumber(inputs.hourlyCostRaw),
    errorRate: parsePercent(inputs.errorRateRaw),
  };

  const fields = Object.keys(parsed) as HubSpotRoiField[];
  const empty = fields.filter((field) => parsed[field] === null);
  const invalid = fields.filter((field) => Number.isNaN(parsed[field]));
  if (empty.length || invalid.length) return { kind: "incomplete", empty, invalid };

  const employees = parsed.employees as number;
  const hoursPerWeek = parsed.hoursPerWeek as number;
  const hourlyCost = parsed.hourlyCost as number;
  const errorRate = parsed.errorRate as number;
  const automationEfficiency = inputs.automationEfficiency;

  const totalManualHoursAnnual =
    employees * hoursPerWeek * HUBSPOT_ROI_WORKING_WEEKS_PER_YEAR;
  const annualHoursSaved = Math.round(totalManualHoursAnnual * automationEfficiency);
  const labourSavings = annualHoursSaved * hourlyCost;
  const errorReductionSavings =
    totalManualHoursAnnual *
    (errorRate / 100) *
    hourlyCost *
    HUBSPOT_ROI_ERROR_COST_MULTIPLIER *
    automationEfficiency;
  const annualCostSavings = Math.round(labourSavings + errorReductionSavings);
  const estimatedImplementationCost = Math.max(
    HUBSPOT_ROI_IMPL_BUDGET_MIN,
    Math.min(HUBSPOT_ROI_IMPL_BUDGET_MAX, 0.4 * annualCostSavings),
  );
  const roiPercentage =
    annualCostSavings > 0
      ? Math.round(
          ((annualCostSavings - estimatedImplementationCost) /
            estimatedImplementationCost) *
            100,
        )
      : 0;
  const paybackMonths =
    annualCostSavings > 0
      ? Math.max(1, Math.round((estimatedImplementationCost / annualCostSavings) * 12))
      : 0;

  return {
    kind: "value",
    kpis: {
      totalManualHoursAnnual,
      annualHoursSaved,
      labourSavings: Math.round(labourSavings),
      errorReductionSavings: Math.round(errorReductionSavings),
      annualCostSavings,
      estimatedImplementationCost,
      roiPercentage,
      paybackMonths,
    },
  };
}
